import { List, Map } from "immutable";
import { nanoid } from "nanoid";

import AppConstants from "@/constants/AppConstants";
import { ELEMENTS, ELEMENT_ORDER, PARENT_ID } from "@/constants/EntityConstants";
import { LAYOUTS } from "@/constants/DesignConstants.mjs";
import {
  positionElementToContainer,
  resizeContainer,
  translateElementToContainerSpace,
} from "@/utils/GroupUtils";
import { gatherProps } from "@/utils/Merge/MergeUtils.mjs";
import PROPERTY_MAPS from "@/constants/PropertyMaps";
import { DROP_AREA_BOTTOM, DROP_AREA_TOP } from "@/constants/LayerConstants";
import { sortByReadingOrder } from "@/utils/ReadingOrderUtils";
import IDUtils from "@/utils/IDUtils";

/**
 * @typedef {import("@/types/ElementTypes.mjs").ImmutableElement} Element
 * @typedef {import("@/types/ElementTypes.mjs").DimensionPosition} DimensionPosition
 * @typedef {Record<typeof LAYOUTS[number], DimensionPosition>} ContainerPositions
 */

/**
 * @typedef {Object} Target
 * @property {string} id - The id of the target element or container.
 * @property {number=} index - (Optional) The index at which to insert or move the element. Contained targets use dropArea instead.
 * @property {string} layerType - The type of layer (e.g., "element", "block").
 * @property {string=} dropArea - (Optional) The drop area, such as "top" or "bottom".
 * @property {string=} containedBy - (Optional) The id of the container that the element is contained by.
 */

const { ID_LENGTH, ITEM_TYPE_ELEMENT } = AppConstants;
const FROM = "from";
const TO = "to";

/**
 * Consolidates element container type element logic like a group or component.
 */
class ElementContainer {
  /**
   *
   * @param {Object} data
   * @param {Object} parent
   * @param {Array<string> | string} path
   */
  constructor(data, parent, path) {
    this.data = data;
    this.parent = parent;
    this.path = Array.isArray(path) ? path : IDUtils.getArray(path);
    this.stringPath = Array.isArray(path) ? IDUtils.getPathID(path) : path;
  }

  /**
   * Contains elements within group. Returns new element paths relative to parent.
   * @param {Array<Element>} elements
   * @param {number=} insertAt
   * @param {boolean} translate - translate element coordinates to container
   * @returns {Array<string>}
   */
  contain(elements, insertAt, translate = true) {
    const containerId = this.data.get("id");

    const containerPosition = this._getOwnPositions();

    const updatedElements = elements.map(
      (elementData) =>
        elementData
          .set(PARENT_ID, containerId)
          .set("id", nanoid(ID_LENGTH))
          .mergeDeep(this._translate(elementData, containerPosition, translate ? TO : undefined)),
      [],
    );

    const elementOrderList = this.getElementOrder();
    const insertionIndex = insertAt ?? 0;
    const elementOrder = elementOrderList.splice(
      insertionIndex,
      0,
      ...updatedElements.map((element) => element.get("id")),
    );

    this.data = this.data.set(ELEMENT_ORDER, elementOrder).set(
      ELEMENTS,
      updatedElements.reduce(
        (result, element) => result.set(element.get("id"), this._cleanChildData(element)),
        this.data.get(ELEMENTS),
      ),
    );

    const updatedPaths = updatedElements.map((element) =>
      this._elementPathFromId(element.get("id")),
    );

    this.resize();

    return updatedPaths;
  }

  /**
   * Inserts elements into the container at the target index.
   * @param {Array<Element>} elements
   * @param {Target} target
   * @returns {Array<string>}
   */
  insertElements(elements, target) {
    const { id, dropArea } = target;
    const elementOrderList = this.getElementOrder();
    const targetElementId = id.split(".").at(-1);
    const targetElementIndex = elementOrderList.indexOf(targetElementId);

    const dropAreaIndex = dropArea === DROP_AREA_TOP ? targetElementIndex : targetElementIndex + 1;

    const newPaths = this.contain(elements, dropAreaIndex);

    return newPaths;
  }

  /**
   * Release element by removing from container and cleaning parent references and return element data.
   * @param {string | Array<string>} elementPath
   * @param {boolean=} translate - translate out of container, adding the parent's offsets to the element's to appear to have not moved.
   * @param {boolean=} asCopy - Implies a translate and returns a design space
   * @return {Element}
   */
  releaseElement(elementPath, translate = true, asCopy = false) {
    const localizedPath = this._localizeElementPath(elementPath);
    const elementData = this.data.getIn(localizedPath);
    const elementId = elementData.get("id");

    // remove element ref and place in order
    this.data = this.data.set(ELEMENTS, this.data.get(ELEMENTS).delete(elementId)).set(
      ELEMENT_ORDER,
      this.getElementOrder().filter((id) => id !== elementId),
    );

    const containerPosition = this._getOwnPositions();

    const releasedElement = elementData
      .delete(PARENT_ID)
      .delete("id")
      .mergeDeep(this._translate(elementData, containerPosition, translate ? FROM : undefined));

    // disable resize in group on remove
    this.getElementCount() > 1 && this.resize();

    return releasedElement;
  }

  /**
   * Detects if paths will remove all elements.
   * @param {Array<Array<string> | string>} paths
   * @returns {boolean}
   */
  pathsRemoveAll(paths) {
    const validPaths = paths.filter((path) =>
      (Array.isArray(path) ? path.join(".") : path).startsWith(this.stringPath),
    );

    return validPaths.length && validPaths.length >= this.getElementCount();
  }

  /**
   * Releases a set of elements
   * @param {Array<string | Array<string>>} elementPaths
   * @param {boolean=} translate
   * @returns {Array<Element>}
   */
  releaseElements(elementPaths, translate = true) {
    return elementPaths.map((path) => this.releaseElement(path, translate));
  }

  /**
   * Translates element coordinates to or from the container
   * @param {Object} elementData
   * @param {ContainerPositions} containerPositions
   * @param {(FROM | TO)=} toFrom
   */
  _translate(elementData, containerPositions, toFrom) {
    return LAYOUTS.reduce((result, layout) => {
      const layoutProps = gatherProps(
        elementData,
        layout,
        PROPERTY_MAPS.ELEMENT_POSITION,
        false,
      ).toJS();

      result[layout] =
        toFrom === TO
          ? translateElementToContainerSpace(layoutProps, containerPositions[layout])
          : toFrom === FROM
            ? positionElementToContainer(layoutProps, containerPositions[layout], true)
            : layoutProps;

      return result;
    }, {});
  }

  /**
   * Gets positions for each layout of the elementContainer
   * @returns {ContainerPositions}
   */
  _getOwnPositions() {
    return LAYOUTS.reduce((result, layout) => {
      result[layout] = gatherProps(this.data, layout, PROPERTY_MAPS.ELEMENT_POSITION, false).toJS();
      return result;
    }, {});
  }

  /**
   * Gathers all element position data for each layout.
   * @returns {Record<string, { [key: typeof LAYOUTS[number]]: DimensionPosition}>}
   */
  _getElementPositions() {
    return this.getElementOrder().reduce((result, id) => {
      const elementData = this._getElementById(id);
      result[this._elementPathFromId(id)] = LAYOUTS.reduce((elementResult, layout) => {
        elementResult[layout] = gatherProps(
          elementData,
          layout,
          PROPERTY_MAPS.ELEMENT_POSITION,
          false,
        ).toJS();
        return elementResult;
      }, {});
      return result;
    }, {});
  }

  /**
   * Returns positions for the container and elements.
   * @returns {{
   *  container: ContainerPositions,
   *  elements: Record<string, { [key: typeof LAYOUTS[number]]: DimensionPosition}>
   * }}
   */
  getPositions() {
    return {
      container: this._getOwnPositions(),
      elements: this._getElementPositions(),
    };
  }

  /**
   * Takes the element path and cuts off the parent's path to make it local to the container.
   * @param {string | Array<string>} path
   */
  _localizeElementPath(path) {
    const elementPath = Array.isArray(path) ? path : path.split(".");
    return elementPath.slice(this.path.length);
  }

  /**
   * Get an element by it's id (not path)
   * @param {string} id
   * @returns {Element}
   */
  _getElementById(id) {
    return this.data.getIn([ELEMENTS, id]);
  }

  /**
   * Reflect own changes to parent.
   */
  applyToParent() {
    this.parent.data = this.parent.data.setIn(this._getInnerPath(), this.data);
  }

  /**
   * Gets all contained element paths.
   * @returns {Array<string>}
   */
  getElementPaths() {
    return this.getElementOrder().map((id) => this._elementPathFromId(id));
  }

  /**
   * Gets a path relative to the container.
   * @returns {Array<string>}
   */
  _getInnerPath() {
    return this.path.slice(1);
  }

  /**
   * Gets a full string path for a contained element
   * @param {string} id
   * @returns {string}
   */
  _elementPathFromId(id) {
    return `${this.stringPath}.${ELEMENTS}.${id}`;
  }

  /**
   * Gets an element's index within element order
   * @param {Array<string> | string} pathOrId - element path or id
   * @returns {number}
   */
  _getElementIndex(pathOrId) {
    const elementId = (Array.isArray(pathOrId) ? pathOrId : pathOrId.split(".")).at(-1);
    return this.getElementOrder().indexOf(elementId);
  }

  /**
   * Returns the element order list
   * @returns {List<string>}
   */
  getElementOrder() {
    return this.data.get(ELEMENT_ORDER);
  }

  getElementCount() {
    return this.getElementOrder().size;
  }

  /**
   * Builds the move target for sending a selection to the front or back of this container.
   * Returns null when the selection already sits at that bound or fills the container.
   * @param {Object} props
   * @param {string} props.baseElemId - path to the elements collection holding the selection
   * @param {boolean} props.isFront
   * @param {Iterable<string>} props.selectedIds - element ids or paths in the selection
   * @returns {Target | null}
   */
  getBoundTarget({ baseElemId, isFront, selectedIds }) {
    const localIds = selectedIds.map((id) => IDUtils.getArray(id).at(-1)).toSet();
    const elementOrder = this.getElementOrder();
    const candidates = elementOrder.filter((id) => !localIds.has(id));

    if (!candidates.size) return null;

    const containedCount = elementOrder.size - candidates.size;
    const targetElemId = isFront ? candidates.first() : candidates.last();
    const boundPosition = isFront ? containedCount : candidates.size - 1;

    if (this._getElementIndex(targetElemId) === boundPosition) return null;

    return {
      id: IDUtils.getID(baseElemId, targetElemId),
      layerType: ITEM_TYPE_ELEMENT,
      containedBy: this.data.get("id"),
      dropArea: isFront ? DROP_AREA_TOP : DROP_AREA_BOTTOM,
    };
  }

  /**
   * Checks element count against expected removal count. Can be checked with empty params during iteration to check if below threshold.
   * @param {number} removalCount
   * @returns {boolean}
   */
  shouldDissolve(removalCount = 0) {
    return this.getElementCount() - removalCount < 2;
  }

  delete() {
    this.parent.data = this.parent.data.deleteIn(this._getInnerPath());
  }

  /**
   * Gets the element with coordinates in design space (as though it were released)
   * @param {string} elementId - The inner element's Id. Not a path.
   * @returns {Element}
   */
  getTranslatedOut(elementId) {
    const elementData = this.data.getIn([ELEMENTS, elementId]);
    const containerPosition = this._getOwnPositions();

    return elementData.mergeDeep(this._translate(elementData, containerPosition, FROM));
  }

  /**
   * Cycles container and element ids to prevent collision.
   */
  cycleIds() {
    const newGroupId = nanoid(ID_LENGTH);

    const [elementOrder, elements] = this.data.get(ELEMENT_ORDER).reduce(
      (result, currenElementId) => {
        const newElementId = nanoid(ID_LENGTH);

        result[0] = result[0].push(newElementId);
        result[1] = result[1].set(
          newElementId,
          this.data
            .getIn([ELEMENTS, currenElementId])
            .set("id", newElementId)
            .set(PARENT_ID, newGroupId),
        );

        return result;
      },
      [List(), Map()],
    );

    this.data = this.data
      .set("id", newGroupId)
      .set(ELEMENT_ORDER, elementOrder)
      .set(ELEMENTS, elements);
    this.applyToParent();
  }

  /**
   * Gets layout data for the container
   * @param {typeof LAYOUTS[number]} layout
   * @returns
   */
  getLayoutPosition(layout) {
    return this.data.get(layout);
  }

  /**
   * Updates an the container's position and dimensions for a layout
   * @param {DimensionPosition} data
   * @param {typeof LAYOUTS[number]} layout
   */
  _updatePosition(data, layout) {
    this.data = this.data.set(layout, this.data.get(layout).merge(data));
  }

  /**
   * Updates an elements position and dimensions for a layout
   * @param {string} id
   * @param {DimensionPosition} data
   * @param {typeof LAYOUTS[number]} layout
   */
  _updateElementPosition(id, data, layout) {
    const elementId = id.split(".").at(-1);
    const elementUpdatePath = [ELEMENTS, elementId, layout];
    this.data = this.data.setIn(elementUpdatePath, this.data.getIn(elementUpdatePath).merge(data));
  }

  /**
   * Checks that the path leads to own contained elements and that the element exists
   * @param {Array<string> | string} path
   * @returns {boolean}
   */
  contains(path) {
    const stringPath = Array.isArray(path) ? path.join(".") : path;
    const elementId = IDUtils.getArray(path).at(-1);
    return stringPath.startsWith(this.stringPath) && this.data.hasIn([ELEMENTS, elementId]);
  }

  /**
   * Reorders paths to match inner order. Assumes all paths are contained by this and that they exist.
   * @param {Array<Array<string> | string>} paths
   * @returns {Array<Array<string> | string>}
   */
  reorderPaths(paths) {
    return paths.toSorted((a, b) => this._getElementIndex(a) - this._getElementIndex(b));
  }

  /**
   * Rebuilds elementOrder so contained elements are in top-to-bottom,
   * left-to-right reading order for the given layout. Recurses into any
   * nested groups.
   *
   * Mutates this.data. Caller must invoke applyToParent() if persistence
   * to the parent entity is required.
   *
   * @param {typeof LAYOUTS[number]} layout
   */
  sortByReadingOrder(layout) {
    const elementsMap = this.data.get(ELEMENTS);
    const orderedElements = this.getElementOrder()
      .map((id) => elementsMap.get(id))
      .filter(Boolean);

    const sorted = sortByReadingOrder(orderedElements, layout);
    const newOrder = sorted.map((el) => el.get("id"));

    this.data = this.data.set(ELEMENT_ORDER, newOrder);

    // Recurse into nested groups.
    newOrder.forEach((childId) => {
      const child = elementsMap.get(childId);
      if (child && child.get(ELEMENTS)) {
        const childContainer = new ElementContainer(child, this, [...this.path, ELEMENTS, childId]);
        childContainer.sortByReadingOrder(layout);
        this.data = this.data.setIn([ELEMENTS, childId], childContainer.data);
      }
    });
  }

  /**
   * Resizes the container based on element size and position. Maintains rotation and position where possible.
   */
  resize() {
    const positions = this.getPositions();

    LAYOUTS.forEach((layout) => {
      const layoutElements = Object.entries(positions.elements).reduce(
        (result, [id, layoutsData]) => {
          result[id] = layoutsData[layout];
          return result;
        },
        {},
      );

      const [resizedContainer, shiftedTargets] = resizeContainer(
        layoutElements,
        positions.container[layout],
      );

      this._updatePosition(resizedContainer, layout);
      Object.entries(shiftedTargets).forEach(([id, layoutData]) => {
        this._updateElementPosition(id, layoutData, layout);
      });

      this.applyToParent();
    });
  }

  /**
   * Cleans all invalid children data in the the container.
   */
  cleanChildren() {
    this.data = this.data.get(ELEMENTS).map(this._cleanChildData);
    this.applyToParent();
  }

  /**
   * Cleans an element's invalid data.
   * @param {Element} childData
   * @returns {Element} - The cleaned element data.
   */
  _cleanChildData(childData) {
    const removeActions = this.data.has("link") || this.data.has("pubs");

    const base = LAYOUTS.reduce((result, layout) => {
      return result.deleteIn([layout, "lockV"]).deleteIn([layout, "lockH"]);
    }, childData);

    return removeActions ? base.delete("pubs").delete("link") : base;
  }
}

export default ElementContainer;
